/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

export type ImageAndTextRowProps = {
	title: string;
	description: string;
	image: {
		src: string;
		srcDark?: string;
		alt: string;
	};
	testId?: string;
	href?: string;
};

const styles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),

		'@media (min-width: 48rem)': {
			alignItems: 'center',
			flexDirection: 'row',
			gap: '0',
		},
	},
	imageContainer: {
		overflow: 'hidden',
		borderRadius: token('radius.xlarge'),
		'@media (min-width: 48rem)': {
			flex: '0 0 50%',
		},
	},
	link: {
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none',
			borderColor: token('color.border.bold'),
		},
	},
	image: {
		display: 'block',
		width: '100%',
	},
	contentContainer: {
		'@media (min-width: 48rem)': {
			flex: '0 0 50%',
			paddingBlockStart: token('space.400'),
			paddingInlineEnd: token('space.400'),
			paddingBlockEnd: token('space.400'),
			paddingInlineStart: token('space.400'),
		},
	},
});

/**
 * __ImageAndTextRow__
 *
 * An `ImageAndTextRow` shows an image alongside text in a row.
 *
 * For multiple rows, use `ImageAndTextRowContainer` to wrap them,
 * which will apply appropriate spacing between the rows.
 *
 */
const ImageAndTextRow = ({
	title,
	description,
	testId,
	image,
	href,
}: ImageAndTextRowProps): JSX.Element => {
	const content = (
		<>
			<Box xcss={styles.imageContainer}>
				<Image
					src={image.src}
					srcDark={image.srcDark}
					alt={image.alt || ''}
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
					css={styles.image}
				/>
			</Box>

			<Box xcss={styles.contentContainer}>
				<Stack space="space.150">
					<Heading size="medium">{title}</Heading>
					<Text as="p">{description}</Text>
				</Stack>
			</Box>
		</>
	);

	return href ? (
		<Anchor href={href} testId={testId} xcss={cx(styles.root, styles.link)}>
			{content}
		</Anchor>
	) : (
		<Box testId={testId} xcss={styles.root}>
			{content}
		</Box>
	);
};

export default ImageAndTextRow;
